import { apiClient } from './api';
import { BaseService } from './BaseService';
import {
  SalesOrder,
  SalesOrderCreate,
  SalesOrderUpdate,
  SalesOrderSearchFilters,
  SalesOrderListResponse,
  SalesOrderStatusUpdate,
  SalesOrderConversionRequest,
  SalesOrderConversionResponse,
  SalesOrderToInvoiceConversionResponse
} from '../types/salesOrder';

class SalesOrderService extends BaseService<SalesOrder, SalesOrderCreate, SalesOrderUpdate, SalesOrderSearchFilters> {
  protected baseUrl = '/admin/sales-orders';

  async createSalesOrder(data: SalesOrderCreate): Promise<SalesOrder> {
    return this.create(data);
  }

  async convertEstimateToSalesOrder(data: SalesOrderConversionRequest): Promise<SalesOrderConversionResponse> {
    const response = await apiClient.post(`${this.baseUrl}/convert-from-estimate`, data);
    return response.data;
  }

  async getSalesOrders(filters: SalesOrderSearchFilters & { page?: number; per_page?: number }): Promise<SalesOrderListResponse> {
    const params = this.buildQueryParams(filters);
    const response = await apiClient.get(`${this.baseUrl}?${params.toString()}`);
    // Backend returns sales_orders, not items
    return response.data;
  }

  async getSalesOrder(id: string): Promise<SalesOrder> {
    return this.getById(id);
  }

  async downloadSalesOrderPDF(id: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getSalesOrderByNumber(orderNumber: string): Promise<SalesOrder> {
    const response = await apiClient.get(`${this.baseUrl}/number/${orderNumber}`);
    return response.data;
  }

  async updateSalesOrder(id: string, data: SalesOrderUpdate): Promise<SalesOrder> {
    return this.update(id, data);
  }

  async deleteSalesOrder(id: string): Promise<void> {
    return this.delete(id);
  }

  async updateStatus(id: string, data: SalesOrderStatusUpdate): Promise<SalesOrder> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, data);
    return response.data;
  }

  async confirmSalesOrder(id: string): Promise<SalesOrder> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/confirm`);
    return response.data;
  }

  async cancelSalesOrder(id: string): Promise<SalesOrder> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/cancel`);
    return response.data;
  }

  async convertToInvoice(id: string, dueDays: number = 30): Promise<SalesOrderToInvoiceConversionResponse> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/convert-to-invoice?due_days=${dueDays}`);
    return response.data;
  }

  // Inherited from BaseService - no need to override
}

export const salesOrderService = new SalesOrderService();